import { useId, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

export interface OtpInputProps {
  label: string;
  /** Current code value (digits only, may be shorter than `length`). */
  value: string;
  onChange: (value: string) => void;
  length?: number;
  /** Boxes per visual group, separated by a dash (Figma: 3-3). */
  groupSize?: number;
  error?: string;
  disabled?: boolean;
}

/**
 * Segmented one-time-code input: one box per digit, grouped with a dash
 * separator. Supports typing, backspace navigation, and pasting a full code.
 */
export function OtpInput({
  label,
  value,
  onChange,
  length = 6,
  groupSize = 3,
  error,
  disabled = false,
}: OtpInputProps) {
  const errorId = useId();
  const boxRefs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const setDigit = (index: number, digit: string) => {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join('').slice(0, length));
  };

  const handleInput = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) {
      setDigit(index, '');
      return;
    }
    // Take the last typed digit for this box, spill any extras forward.
    const spill = typed.slice(0, length - index);
    const next = digits.slice();
    for (let offset = 0; offset < spill.length; offset += 1) {
      next[index + offset] = spill[offset];
    }
    onChange(next.join('').slice(0, length));
    boxRefs.current[Math.min(index + spill.length, length - 1)]?.focus();
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      setDigit(index - 1, '');
      boxRefs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      boxRefs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      boxRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    boxRefs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        role="group"
        aria-label={label}
        aria-describedby={error ? errorId : undefined}
        className="flex items-center gap-3"
      >
        {digits.map((digit, index) => (
          <span key={index} className="flex items-center gap-3">
            {index > 0 && index % groupSize === 0 && (
              <span aria-hidden="true" className="text-slate">
                &ndash;
              </span>
            )}
            <input
              ref={(el) => {
                boxRefs.current[index] = el;
              }}
              value={digit}
              onChange={(event) => handleInput(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onPaste={handlePaste}
              onFocus={(event) => event.target.select()}
              disabled={disabled}
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              aria-label={`Digit ${index + 1}`}
              aria-invalid={Boolean(error)}
              className={`h-14 w-14 rounded-button border text-center font-sans text-xl font-semibold text-navy focus:border-transparent focus:outline-none focus:ring-2 focus:ring-dkgreen disabled:opacity-60 ${
                error ? 'border-danger' : digit ? 'border-green' : 'border-input-border'
              }`}
            />
          </span>
        ))}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
